import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import "../css/globalPopupProvider.css";

interface PopupContextType {
  isOpen: boolean;
  content: ReactNode;
  openPopup: (content: ReactNode) => void;
  closePopup: () => void;
  openConfirm: (content: ReactNode) => void;
  closeConfirm: () => void;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

const ANIMATION_DURATION = 220;

export const GlobalPopupProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen]   = useState(false);
  const [content, setContent] = useState<ReactNode>(null);
  const [phase, setPhase]     = useState<'entering' | 'visible' | 'leaving' | 'hidden'>('hidden');

  // ── Confirm layer ──
  const [confirmNode, setConfirmNode]   = useState<ReactNode>(null);
  const [confirmPhase, setConfirmPhase] = useState<'entering' | 'visible' | 'leaving' | 'hidden'>('hidden');

  const timerRef        = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearConfirmTimer = useCallback(() => {
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
  }, []);

  const closeConfirm = useCallback(() => {
    clearConfirmTimer();
    setConfirmPhase('leaving');
    confirmTimerRef.current = setTimeout(() => {
      setConfirmNode(null);
      setConfirmPhase('hidden');
    }, ANIMATION_DURATION);
  }, [clearConfirmTimer]);

  const openPopup = useCallback((popupContent: ReactNode) => {
    clearTimer();
    setContent(popupContent);
    setIsOpen(true);
    setPhase('hidden');
    requestAnimationFrame(() => {
      setPhase('entering');
      requestAnimationFrame(() => setPhase('visible'));
    });
  }, [clearTimer]);

  const closePopup = useCallback(() => {
    clearTimer();
    closeConfirm();
    setPhase('leaving');
    timerRef.current = setTimeout(() => {
      setIsOpen(false);
      setContent(null);
      setPhase('hidden');
    }, ANIMATION_DURATION);
  }, [clearTimer, closeConfirm]);

  const openConfirm = useCallback((confirmContent: ReactNode) => {
    clearConfirmTimer();
    setConfirmNode(confirmContent);
    setConfirmPhase('hidden');
    requestAnimationFrame(() => {
      setConfirmPhase('entering');
      requestAnimationFrame(() => setConfirmPhase('visible'));
    });
  }, [clearConfirmTimer]);

  // ── Cleanup timers on unmount ──────────────────────────────────────────────
  useEffect(() => () => {
    clearTimer();
    clearConfirmTimer();
  }, [clearTimer, clearConfirmTimer]);

  const isRendered        = phase        !== 'hidden';
  const isConfirmRendered = confirmPhase !== 'hidden';

  // ── Lock body scroll whenever any overlay is visible ──────────────────────
  // Restored on close or if this component unmounts while a popup is open
  useEffect(() => {
    if (isRendered || isConfirmRendered) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isRendered, isConfirmRendered]);
  return (
    <PopupContext.Provider value={{ isOpen, content, openPopup, closePopup, openConfirm, closeConfirm }} //NOSONAR
    >
      {children}

      {/* ── Main popup overlay ── */}
      {isRendered && (
        <div  //NOSONAR
          className={`scl--popup-overlay-context-global scl--popup-overlay--${phase}`}
          onClick={closePopup}
        >
          <div  //NOSONAR
            className={`scl--popup-content-context-global scl--popup-content--${phase}`}
            onClick={(e) => e.stopPropagation()}
          >
            {content}
          </div>
        </div>
      )}

      {/* ── Confirm layer — rendered outside overlay, fixed to viewport ── */}
      {isConfirmRendered && (
        <div  //NOSONAR
          className={`scl--popup-confirm-layer scl--popup-confirm--${confirmPhase}`}
          onClick={ /* istanbul ignore next */ (e) => /* istanbul ignore next */ e.stopPropagation()}
        >
          {confirmNode}
        </div>
      )}

    </PopupContext.Provider>
  );
};

export const useGlobalPopupProvider = () => {
  const context = useContext(PopupContext);
  if (!context) throw new Error("usePopup must be used within a PopupProvider");
  return context;
};